import { Suspense, lazy, useEffect, useState } from 'react';
import { m, AnimatePresence, LazyMotion, domAnimation } from 'motion/react';
import { CheckCircle2 } from 'lucide-react';
import Header from './components/Header';
import PaceCalculatorView from './components/PaceCalculatorView';
import SharePreviewModal from './components/SharePreviewModal';
import { AnalyticsConsent } from './components/AnalyticsConsent';
import { trackPage } from './utils/analytics';
import UpdateToast from './components/UpdateToast';
import GoalBanner from './components/GoalBanner';
import Footer from './components/Footer';
import { RunLogEntry } from './types';
import { calculateTime } from './utils/calculator';
import { t, tf } from './utils/i18n';
import { readStored, writeStored } from './utils/storage';
import { parseSharePayload, SharePayload } from './utils/sharePayload';
import { parseGoal, RaceGoal } from './utils/goal';
import { STANDARD_RACES } from './utils/performance';
import { raceName } from './components/GoalCard';
import { usePersistentState } from './hooks/usePersistentState';
import { useSettings } from './hooks/useSettings';
import { useCalculator } from './hooks/useCalculator';
import { useRunLog } from './hooks/useRunLog';
import { useToast } from './hooks/useToast';
import { useOnlineStatus } from './hooks/useOnlineStatus';

// Vensters worden pas geladen als ze voor het eerst nodig zijn
const SettingsModal = lazy(() => import('./components/SettingsModal'));
const InstallModal = lazy(() => import('./components/InstallModal'));
const LogbookModal = lazy(() => import('./components/LogbookModal'));
const SplitsModal = lazy(() => import('./components/SplitsModal'));
const PerformanceModal = lazy(() => import('./components/PerformanceModal'));
const OnboardingModal = lazy(() => import('./components/OnboardingModal'));

export default function App() {
  const {
    language, setLanguage,
    distanceUnit, setDistanceUnit,
    paceOrSpeedMode, setPaceOrSpeedMode,
    themeMode, setThemeMode,
    themeColor, setThemeColor,
  } = useSettings();
  const {
    calcTarget, setCalcTarget,
    distanceKm, setDistanceKm,
    paceSecsPerKm, setPaceSecsPerKm,
    totalSeconds, setTotalSeconds,
    setAll: handleUpdateRunnerState,
  } = useCalculator();
  const { logs, saveRun, addRun, updateRun, deleteRun, restoreRun, replaceRuns } = useRunLog();
  const { toast, showToast } = useToast();
  const [goal, setGoal] = usePersistentState<RaceGoal | null>('pace_goal', null, parseGoal);
  const isOffline = useOnlineStatus(() => showToast(t(language, 'backOnlineToast'), 2500));

  // Modal open states
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [isLogbookModalOpen, setIsLogbookModalOpen] = useState(false);
  const [isSplitsModalOpen, setIsSplitsModalOpen] = useState(false);
  const [isPerformanceOpen, setIsPerformanceOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(
    () => !readStored<boolean>('pace_onboarding_completed', false, (raw) => (raw === true || raw === 'true' ? true : undefined)),
  );

  // Schermen zonder router: elke wissel telt als paginaweergave in Google Analytics.
  const screen = isSettingsOpen ? ['/instellingen', 'Instellingen']
    : isInstallModalOpen ? ['/installeren', 'Installeren']
    : isLogbookModalOpen ? ['/logboek', 'Logboek']
    : isSplitsModalOpen ? ['/splits', 'Splits']
    : isPerformanceOpen ? ['/prestaties', 'Prestaties']
    : ['/' + (calcTarget === 'time' ? '' : calcTarget), 'PaceFlow'];
  useEffect(() => {
    trackPage(screen[0], screen[1]);
  }, [screen[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  // PWA install prompt (window.deferredPWAEvent blijft beschikbaar volgens AGENTS.md)
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      (window as any).deferredPWAEvent = e;
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleTriggerInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstallModalOpen(false);
      }
    } else {
      setIsInstallModalOpen(true);
    }
  };

  // URL State Sharing: eerst een preview tonen, pas na bevestiging overnemen.
  const [pendingShare, setPendingShare] = useState<SharePayload | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareParam = urlParams.get('share') || urlParams.get('calc');
    if (!shareParam) return;
    const payload = parseSharePayload(shareParam);
    if (payload) {
      setPendingShare(payload);
    } else {
      showToast(t(language, 'shareInvalidToast'), 3500);
    }
    // Ruim de URL op zonder de pagina te herladen
    window.history.replaceState({}, document.title, window.location.pathname);
    // Alleen bij het opstarten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAcceptShare = () => {
    if (!pendingShare) return;
    handleUpdateRunnerState(pendingShare.distanceKm, pendingShare.paceSecsPerKm, pendingShare.totalSeconds);
    if (pendingShare.calcTarget) setCalcTarget(pendingShare.calcTarget);
    if (pendingShare.distanceUnit) setDistanceUnit(pendingShare.distanceUnit);
    setPendingShare(null);
    showToast(t(language, 'shareLoadedToast'), 3500);
  };

  // Logboek-acties met terugkoppeling: record-melding en ongedaan maken na verwijderen
  const announceRecord = (categoryId: string | null) => {
    if (!categoryId) return;
    const race = STANDARD_RACES.find((r) => r.id === categoryId);
    if (race) showToast(tf(language, 'newRecordToast', { distance: raceName(language, race) }), 4000);
  };

  const handleSaveRun: typeof saveRun = (entry) => {
    const record = saveRun(entry);
    announceRecord(record);
    return record;
  };

  const handleAddRun: typeof addRun = (entry) => {
    const record = addRun(entry);
    announceRecord(record);
    return record;
  };

  const handleUpdateRun = (entry: RunLogEntry) => {
    updateRun(entry);
    showToast(t(language, 'runUpdatedToast'), 2500);
  };

  const handleDeleteRun = (id: string) => {
    const removed = deleteRun(id);
    if (!removed) return;
    showToast(t(language, 'runDeletedToast'), 6000, {
      label: t(language, 'undoBtn'),
      onClick: () => {
        restoreRun(removed);
        showToast(t(language, 'runRestoredToast'), 2500);
      },
    });
  };

  const handleLoadRunToCalculator = (entry: RunLogEntry) => {
    handleUpdateRunnerState(entry.distanceKm, entry.paceSecondsPerKm, entry.totalSeconds);
    showToast(t(language, 'valuesLoadedToast'), 2800);
  };

  return (
    <LazyMotion features={domAnimation} strict>
    <div className="min-h-screen text-slate-900 dark:text-[#F0F0F5] transition-colors duration-200">
      {/* 1. PaceFlow Top App Bar with Integrated Sticky Segmented Control */}
      <Header
        language={language}
        logCount={logs.length}
        calcTarget={calcTarget}
        onChangeCalcTarget={setCalcTarget}
        themeColor={themeColor}
        paceOrSpeedMode={paceOrSpeedMode}
        onOpenSettings={() => setIsSettingsOpen(true)}
        onOpenLogbook={() => setIsLogbookModalOpen(true)}
        onOpenSplits={() => setIsSplitsModalOpen(true)}
      />

      {/* 2. Main Dynamic Content Area: Calculator with Steppers, Presets, and Predictions */}
      <main className="max-w-2xl mx-auto px-4 pt-4 pb-4">
        {goal && (
          <div className="mb-4">
            <GoalBanner
              language={language}
              goal={goal}
              logs={logs}
              distanceUnit={distanceUnit}
              onOpen={() => setIsLogbookModalOpen(true)}
            />
          </div>
        )}
        <PaceCalculatorView
          themeColor={themeColor}
          language={language}
          distanceKm={distanceKm}
          paceSecsPerKm={paceSecsPerKm}
          totalSeconds={totalSeconds}
          calcTarget={calcTarget}
          distanceUnit={distanceUnit}
          paceOrSpeedMode={paceOrSpeedMode}
          onChangeCalcTarget={setCalcTarget}
          onUpdateRunnerState={handleUpdateRunnerState}
          onSaveToLogbook={handleSaveRun}
          onGoToSplits={() => setIsSplitsModalOpen(true)}
          onOpenPerformance={() => setIsPerformanceOpen(true)}
        />

        {/* Universal Footer Branding */}
        <Footer />
      </main>

      {/* 3. Modals & Drawers */}
      {isSettingsOpen && (
        <Suspense fallback={null}>
          <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        language={language}
        onChangeLanguage={setLanguage}
        themeMode={themeMode}
        onChangeThemeMode={setThemeMode}
        themeColor={themeColor}
        onChangeThemeColor={setThemeColor}
        distanceUnit={distanceUnit}
        onChangeDistanceUnit={setDistanceUnit}
        paceOrSpeedMode={paceOrSpeedMode}
        onChangePaceOrSpeedMode={setPaceOrSpeedMode}
        deferredPrompt={deferredPrompt}
        onTriggerInstall={handleTriggerInstall}
        onOpenInstallModal={() => {
          setIsSettingsOpen(false);
          setIsInstallModalOpen(true);
        }}
          />
        </Suspense>
      )}

      {isInstallModalOpen && (
        <Suspense fallback={null}>
          <InstallModal
        isOpen={isInstallModalOpen}
        onClose={() => setIsInstallModalOpen(false)}
        language={language}
        deferredPrompt={deferredPrompt}
        onTriggerInstall={handleTriggerInstall}
        themeColor={themeColor}
          />
        </Suspense>
      )}

      {isLogbookModalOpen && (
        <Suspense fallback={null}>
          <LogbookModal
        isOpen={isLogbookModalOpen}
        onClose={() => setIsLogbookModalOpen(false)}
        language={language}
        themeColor={themeColor}
        logs={logs}
        distanceUnit={distanceUnit}
        goal={goal}
        onChangeGoal={setGoal}
        onUpdateLog={handleUpdateRun}
        onDeleteLog={handleDeleteRun}
        onAddLog={handleAddRun}
        onImportLogs={replaceRuns}
        onLoadRunToCalculator={handleLoadRunToCalculator}
          />
        </Suspense>
      )}

      {isSplitsModalOpen && (
        <Suspense fallback={null}>
          <SplitsModal
        isOpen={isSplitsModalOpen}
        onClose={() => setIsSplitsModalOpen(false)}
        language={language}
        themeColor={themeColor}
        distanceKm={distanceKm}
        paceSecsPerKm={paceSecsPerKm}
        totalSeconds={totalSeconds}
        distanceUnit={distanceUnit}
        onChangeDistance={(d) => {
          setDistanceKm(d);
          setTotalSeconds(calculateTime(d, paceSecsPerKm));
        }}
        onChangePace={(p) => {
          setPaceSecsPerKm(p);
          setTotalSeconds(calculateTime(distanceKm, p));
        }}
          />
        </Suspense>
      )}

      {isPerformanceOpen && (
        <Suspense fallback={null}>
          <PerformanceModal
        isOpen={isPerformanceOpen}
        onClose={() => setIsPerformanceOpen(false)}
        language={language}
        themeColor={themeColor}
        distanceKm={distanceKm}
        totalSeconds={totalSeconds}
        paceSecsPerKm={paceSecsPerKm}
        distanceUnit={distanceUnit}
        logs={logs}
          />
        </Suspense>
      )}

      {isOnboardingOpen && (
        <Suspense fallback={null}>
          <OnboardingModal
        isOpen={isOnboardingOpen}
        onComplete={() => {
          writeStored('pace_onboarding_completed', 'true');
          setIsOnboardingOpen(false);
        }}
        language={language}
        onChangeLanguage={setLanguage}
        distanceUnit={distanceUnit}
        onChangeDistanceUnit={setDistanceUnit}
        paceOrSpeedMode={paceOrSpeedMode}
        onChangePaceOrSpeedMode={setPaceOrSpeedMode}
        themeMode={themeMode}
        onChangeThemeMode={setThemeMode}
        themeColor={themeColor}
          />
        </Suspense>
      )}

      <SharePreviewModal
        payload={pendingShare}
        language={language}
        distanceUnit={distanceUnit}
        onAccept={handleAcceptShare}
        onDismiss={() => setPendingShare(null)}
      />

      <AnalyticsConsent language={language} />
      <UpdateToast language={language} />

      {/* Non-blocking Offline Indicator */}
      <AnimatePresence>
        {isOffline && (
          <m.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 15 }}
            className="fixed bottom-3 left-4 z-40 flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/90 dark:bg-slate-800/90 backdrop-blur-md text-amber-300 text-xs font-medium shadow-md border border-white/10"
          >
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span>{t(language, 'offlineBanner')}</span>
          </m.div>
        )}
      </AnimatePresence>

      {/* Floating Snackbar / Toast (met optionele actie, bijv. ongedaan maken) */}
      <AnimatePresence>
        {toast && (
          <m.div
            role="status"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[150] flex items-center gap-2.5 pl-4 ${
              toast.action ? 'pr-2' : 'pr-4'
            } py-2 min-h-12 rounded-2xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-2xl border border-white/10 dark:border-black/10 text-sm font-bold select-none ${
              toast.action ? '' : 'pointer-events-none'
            }`}
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-400 dark:text-emerald-600 shrink-0 stroke-[2.5]" />
            <span>{toast.text}</span>
            {toast.action && (
              <button
                onClick={() => {
                  toast.action!.onClick();
                }}
                className="min-h-11 px-4 rounded-xl bg-[#7C3AED] text-white whitespace-nowrap active:scale-95 transition-all duration-150 cursor-pointer"
              >
                {toast.action.label}
              </button>
            )}
          </m.div>
        )}
      </AnimatePresence>
    </div>
    </LazyMotion>
  );
}
